import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useNotifications, Notification } from './useNotifications';

export interface WorkspaceMessageItem {
  id: string;
  sender_id: string;
  sender_name: string;
  sender_email: string;
  recipient_id: string | null;
  content: string;
  is_read: boolean;
  created_at: string;
}

export interface AccessRequestItem {
  id: string;
  requested_by: string;
  requester_name: string;
  requester_email: string;
  message: string | null;
  status: string;
  created_at: string;
}

export type InboxCategory = 'all' | 'messages' | 'requests' | 'mentions';

export function useUnifiedInbox(workspaceId: string | null) {
  const { user } = useAuth();
  const notificationsHook = useNotifications(workspaceId);

  const [messages, setMessages] = useState<WorkspaceMessageItem[]>([]);
  const [accessRequests, setAccessRequests] = useState<AccessRequestItem[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(false);

  // Fetch unread workspace messages
  useEffect(() => {
    if (!user || !workspaceId) return;

    async function fetchMessages() {
      setMessagesLoading(true);
      try {
        const { data, error } = await supabase
          .from('workspace_messages')
          .select(`
            id,
            sender_id,
            recipient_id,
            content,
            is_read,
            created_at,
            sender_profile:sender_id (name, email)
          `)
          .eq('workspace_id', workspaceId!)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;

        setMessages(
          (data || []).map((m: any) => ({
            id: m.id,
            sender_id: m.sender_id,
            sender_name: m.sender_profile?.name || '',
            sender_email: m.sender_profile?.email || '',
            recipient_id: m.recipient_id,
            content: m.content,
            is_read: m.is_read,
            created_at: m.created_at,
          }))
        );
      } catch (err) {
        console.error('Failed to load inbox messages:', err);
      } finally {
        setMessagesLoading(false);
      }
    }

    fetchMessages();
  }, [user, workspaceId]);

  // Fetch pending access requests (admin/owner only)
  useEffect(() => {
    if (!user || !workspaceId) return;

    async function fetchRequests() {
      setRequestsLoading(true);
      try {
        const { data, error } = await supabase
          .from('access_requests')
          .select(`
            id,
            message,
            status,
            created_at,
            requested_by,
            profiles:requested_by (name, email)
          `)
          .eq('workspace_id', workspaceId!)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (error) {
          // User might not be admin - silently ignore RLS errors
          if (error.code !== 'PGRST116') {
            console.error('Failed to load access requests:', error);
          }
          setAccessRequests([]);
          return;
        }

        setAccessRequests(
          (data || []).map((r: any) => ({
            id: r.id,
            requested_by: r.requested_by,
            requester_name: r.profiles?.name || '',
            requester_email: r.profiles?.email || '',
            message: r.message,
            status: r.status,
            created_at: r.created_at,
          }))
        );
      } catch (err) {
        console.error('Failed to load access requests:', err);
      } finally {
        setRequestsLoading(false);
      }
    }

    fetchRequests();
  }, [user, workspaceId]);

  // Filter notifications by category
  const categorizedNotifications = useMemo(() => {
    const all = notificationsHook.notifications;
    return {
      all,
      messages: all.filter((n) => n.type === 'workspace_message'),
      mentions: all.filter((n) => n.type === 'mention' || n.type === 'comment'),
      requests: all.filter((n) => n.type === 'access_request'),
    };
  }, [notificationsHook.notifications]);

  // Combined unread count
  const unreadMessages = messages.filter((m) => !m.is_read && m.sender_id !== user?.id).length;
  const pendingRequests = accessRequests.length;
  const unreadMentions = categorizedNotifications.mentions.filter((n) => !n.is_read).length;

  const totalUnread = notificationsHook.unreadCount + unreadMessages + pendingRequests;

  return {
    // Notifications
    notifications: notificationsHook.notifications,
    categorizedNotifications,
    markAsRead: notificationsHook.markAsRead,
    markAllAsRead: notificationsHook.markAllAsRead,
    notificationsLoading: notificationsHook.loading,

    // Messages
    messages,
    unreadMessages,
    messagesLoading,

    // Access Requests
    accessRequests,
    pendingRequests,
    requestsLoading,

    // Mentions
    unreadMentions,

    // Combined
    totalUnread,
    loading: notificationsHook.loading || messagesLoading || requestsLoading,
  };
}
